import { createMemo, onCleanup, onMount } from "solid-js"
import { createStore } from "solid-js/store"
import { createSimpleContext } from "@openanalyst-ai/ui/context"
import { usePlatform } from "@/context/platform"
import { configureCreditBalanceFetch } from "@/hooks/use-credits"
import { configureTranscriptionFetch } from "@/services/transcription"
import { configureBillingFetch } from "@/services/billing"
import { configureMcpFetch } from "@/services/mcp"
import { configureOnboardingFetch } from "@/services/onboarding"
import { configureOnboardingV2Fetch, markOnboardingV2Completed } from "@/services/onboarding-v2"
import { clearAuthStarterHandoff, receiveAuthStarterHandoff } from "@/services/auth-starter-handoff"
import { loadOnboardingFullName } from "@/services/onboarding-profile"
import * as api from "./api"
import * as storage from "./storage"
import {
  clearAccessToken,
  consumeLastRefreshFailure,
  getAccessToken,
  refreshAccessToken,
  configureDesktopRefresh,
  resetDesktopRefresh,
} from "./token"
import { configureDesktopAccountFetch, configureDesktopMode } from "./api"
import { isTransientAuthError } from "@/utils/network-error"
import {
  authConnectionOperation,
  authConnectionReasonForOperation,
  createAuthConnectionIssue,
  tagAuthConnectionOperation,
} from "./connection-issue"
import type {
  AuthConnectionIssueReason,
  AuthConnectionOperation,
  AuthContextValue,
  AuthIntentPreview,
  AuthSession,
  AuthState,
  AuthUser,
  PasswordlessIntent,
  PendingDesktopOnboarding,
  SignUpInput,
} from "./types"

const REFRESH_BUFFER_MS = 2 * 60 * 1000
/** First retry after a refresh we could not reach the network for; doubles up to the max. */
const REFRESH_RETRY_BASE_MS = 5 * 1000
const REFRESH_RETRY_MAX_MS = 60 * 1000
const LOGIN_PATH = "/login"
const PROFILE_CACHE_KEY = "openanalyst.auth.profile_cache"
const DEV_AUTH_BYPASS = import.meta.env.DEV && import.meta.env.VITE_REQUIRE_AUTH !== "1"

function authIntentErrorCode(error: unknown) {
  if (error instanceof api.AuthApiError) return error.code
  if (!(error instanceof Error)) return null
  const code = error.message.split(":")[0]?.trim()
  return code?.startsWith("auth_intent_") ? code : null
}

// Codes the signin/signup split branches on. Desktop prefixes them onto the
// thrown Error message (`<code>: <message>`) so they survive the IPC boundary;
// web surfaces them as AuthApiError.code. This parser reads either shape.
const PASSWORDLESS_INTENT_ERROR_CODES = [
  "user_not_found",
  "username_exists",
  "user_not_confirmed",
  "invalid_code",
  "rate_limited",
] as const
const INTERNAL_AUTH_MESSAGE_TERMS = [
  "authorization header",
  "bearer",
  "control-plane",
  "id_token",
  "access_token",
  "jwt",
  "openanalyst-api",
  "session token",
  "token",
  "wrong_audience",
]
export type PasswordlessIntentErrorCode = (typeof PASSWORDLESS_INTENT_ERROR_CODES)[number]

export function passwordlessIntentErrorCode(error: unknown): PasswordlessIntentErrorCode | null {
  const codes = PASSWORDLESS_INTENT_ERROR_CODES as readonly string[]
  if (error instanceof api.AuthApiError && error.code && codes.includes(error.code)) {
    return error.code as PasswordlessIntentErrorCode
  }
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : null
  if (!message) return null
  const prefix = message.split(":")[0]?.trim()
  return prefix && codes.includes(prefix) ? (prefix as PasswordlessIntentErrorCode) : null
}

function internalAuthMessage(message: string) {
  const lower = message.toLowerCase()
  return INTERNAL_AUTH_MESSAGE_TERMS.some((term) => lower.includes(term))
}

function customerAuthMessage(message: string, fallback: string) {
  const stripped = message
    .replace(/^Error invoking remote method '[^']+':\s*/i, "")
    .replace(/^Error:\s*/i, "")
    .trim()
  if (!stripped) return fallback
  const lower = stripped.toLowerCase()
  if (lower.includes("verification code is invalid") || lower.includes("invalid verification code")) {
    return "Verification code is invalid. Enter the latest code sent to your email."
  }
  if (internalAuthMessage(stripped)) return fallback
  return stripped
}

function toMessage(error: unknown, fallback: string) {
  const intentCode = passwordlessIntentErrorCode(error)
  if (intentCode === "user_not_found") return "No account exists for this email. Please sign up first."
  if (intentCode === "username_exists") return "An account already exists. Please sign in instead."
  if (intentCode === "user_not_confirmed") return "Email is not verified yet. Complete signup verification first."
  if (intentCode === "invalid_code") return "Verification code is invalid. Enter the latest code sent to your email."
  if (intentCode === "rate_limited") return "Too many attempts. Please try again later."
  const authIntentCode = authIntentErrorCode(error)
  if (authIntentCode === "auth_intent_invalid") return "This sign-in link is invalid. Start again from the source that sent you here."
  if (authIntentCode === "auth_intent_expired") return "This sign-in link expired. Start again to request a fresh code."
  if (authIntentCode === "auth_intent_consumed") return "This sign-in link was already used. Start again to request a fresh code."
  if (error instanceof api.AuthApiError) {
    if (error.code === "MISSING_SESSION_TOKEN") {
      return "Password sign-in could not start. Restart OpenAnalyst and try again."
    }
    const msg = error.message.toLowerCase()
    if (msg.includes("duplicate key") || msg.includes("dup key") || msg.includes("e11000")) {
      return "An account with this email already exists. Please sign in."
    }
    return customerAuthMessage(error.message, fallback)
  }
  if (typeof error === "string" && error.trim()) return customerAuthMessage(error, fallback)
  if (error instanceof Error && error.message) return customerAuthMessage(error.message, fallback)
  return fallback
}

function mapUser(session: AuthSession, profile: api.AuthProfile): AuthUser {
  const first = profile.firstName?.trim()
  const last = profile.lastName?.trim()
  const joined = [first, last].filter(Boolean).join(" ").trim()
  return {
    userId: profile.userId || profile._id || session.userId,
    email: profile.email || "",
    fullName: profile.fullName || joined || undefined,
    orgId: profile.orgId || session.orgId,
    workspaceId: session.workspaceId,
    projectId: session.projectId,
    role: session.role,
  }
}

type ControllerDeps = {
  initAuth: typeof api.initAuth
  authenticateWithPassword: typeof api.authenticateWithPassword
  startPasswordlessSignIn: typeof api.startPasswordlessSignIn
  confirmPasswordlessSignIn: typeof api.confirmPasswordlessSignIn
  checkSession: typeof api.checkSession
  getUserProfile: typeof api.getUserProfile
  refreshAccess: typeof api.refreshAccess
  signInWithPassword: typeof api.signInWithPassword
  signInWithGoogle: typeof api.signInWithGoogle
  signUp: typeof api.signUp
  forgotPassword: typeof api.forgotPassword
  resetPassword: typeof api.resetPassword
  verifyEmail: typeof api.verifyEmail
  resendVerification: typeof api.resendVerification
  logout: typeof api.logout
  getSessionToken: typeof storage.getSessionToken
  clearSessionToken: typeof storage.clearSessionToken
  storeReturnTo: typeof storage.storeReturnTo
  clearAllAuthData: typeof storage.clearAllAuthData
  broadcastLogout: typeof storage.broadcastLogout
  onLogoutBroadcast: typeof storage.onLogoutBroadcast
  isDesktop: boolean
  restoreDesktopToken: () => Promise<void>
  navigate: (url: string) => void
  now: () => number
  setTimeout: (handler: () => void, timeout?: number) => ReturnType<typeof setTimeout>
  clearTimeout: (timer: ReturnType<typeof setTimeout>) => void
  log: (message: string, error?: unknown) => void
}

function defaultDeps(): ControllerDeps {
  return {
    initAuth: api.initAuth,
    authenticateWithPassword: api.authenticateWithPassword,
    startPasswordlessSignIn: api.startPasswordlessSignIn,
    confirmPasswordlessSignIn: api.confirmPasswordlessSignIn,
    checkSession: api.checkSession,
    getUserProfile: api.getUserProfile,
    refreshAccess: api.refreshAccess,
    signInWithPassword: api.signInWithPassword,
    signInWithGoogle: api.signInWithGoogle,
    signUp: api.signUp,
    forgotPassword: api.forgotPassword,
    resetPassword: api.resetPassword,
    verifyEmail: api.verifyEmail,
    resendVerification: api.resendVerification,
    logout: api.logout,
    getSessionToken: storage.getSessionToken,
    clearSessionToken: storage.clearSessionToken,
    storeReturnTo: storage.storeReturnTo,
    clearAllAuthData: storage.clearAllAuthData,
    broadcastLogout: storage.broadcastLogout,
    onLogoutBroadcast: storage.onLogoutBroadcast,
    isDesktop: false,
    restoreDesktopToken: async () => {},
    navigate: (url) => {
      if (typeof window === "undefined") return
      window.location.href = url
    },
    now: () => Date.now(),
    setTimeout: (handler, timeout) => setTimeout(handler, timeout),
    clearTimeout: (timer) => clearTimeout(timer),
    log: (message, error) => console.error(message, error),
  }
}

export type AuthController = AuthContextValue & {
  initialize: () => Promise<void>
  destroy: () => void
}

function isTerminalAuthIntentError(error: unknown) {
  const code = authIntentErrorCode(error)
  return code === "auth_intent_invalid" || code === "auth_intent_expired" || code === "auth_intent_consumed"
}

function passwordlessRetrySession(error: unknown) {
  if (!(error instanceof api.AuthApiError)) return null
  if (!error.metadata || typeof error.metadata !== "object" || Array.isArray(error.metadata)) return null
  const session = (error.metadata as { session?: unknown }).session
  return typeof session === "string" && session.trim() ? session.trim() : null
}

function desktopOnboardingData(error: unknown): PendingDesktopOnboarding | null {
  if (!(error instanceof api.AuthApiError) || error.code !== api.OPENANALYST_ONBOARDING_REQUIRED) return null
  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== "object" || (data as { status?: unknown }).status !== "required") return null
  return data as PendingDesktopOnboarding
}

function passwordlessStartReturnedToken(data: api.PasswordlessStartResult) {
  return Boolean(
    data.accessToken?.trim() ||
    data.access_token?.trim() ||
    data.openAnalyst?.accessToken?.trim() ||
    data.openAnalyst?.access_token?.trim() ||
    data.controlPlane?.accessToken?.trim() ||
    data.controlPlane?.access_token?.trim(),
  )
}

export function createAuthController(input: Partial<ControllerDeps> = {}): AuthController {
  const deps = {
    ...defaultDeps(),
    ...input,
    // Desktop has no renderer refresh cookie. Its timer must use the native broker that owns
    // the durable refresh token; otherwise a successful main-process refresh can still be
    // followed by the renderer's cookie route returning 401 and clearing the visible session.
    ...(input.isDesktop && input.refreshAccess === undefined
      ? {
          refreshAccess: async () => {
            if (await refreshAccessToken()) return
            const failure = consumeLastRefreshFailure()
            if (failure) throw failure
            throw new Error("Could not reach OpenAnalyst to confirm your session")
          },
        }
      : {}),
  }
  const [state, setState] = createStore<AuthState>({
    user: null,
    session: null,
    loading: true,
    initialized: false,
    authenticated: false,
    offline: false,
    connectionIssue: null,
    error: null,
    errorDetail: null,
    pendingVerification: null,
    pendingPasswordless: null,
    pendingDesktopOnboarding: null,
  })

  let timer: ReturnType<typeof setTimeout> | null = null
  /** Consecutive refresh attempts lost to an unreachable network. Reset by any successful schedule. */
  let refreshRetries = 0
  let unlisten: (() => void) | null = null
  let unlistenConnectivity: (() => void) | null = null
  let pending = ""
  let everAuthenticated = false

  const ready = createMemo(() => state.initialized)

  const clearTimer = () => {
    if (!timer) return
    deps.clearTimeout(timer)
    timer = null
  }

  const setAuthenticated = (session: AuthSession, user: AuthUser) => {
    everAuthenticated = true
    // Backfill fullName from the onboarding-profile localStorage stash
    // when the backend's profile has none. Set during sign-in by the v3
    // sign-in screen; persists across logout/refresh/restart. No-op
    // when the backend already returned a fullName.
    const userWithName: AuthUser = (() => {
      if ((user.fullName ?? "").trim()) return user
      const stashed = loadOnboardingFullName()
      if (!stashed) return user
      return { ...user, fullName: stashed }
    })()
    setState({
      user: userWithName,
      session,
      loading: false,
      initialized: true,
      authenticated: true,
      offline: false,
      connectionIssue: null,
      error: null,
      errorDetail: null,
      pendingVerification: null,
      pendingPasswordless: null,
      pendingDesktopOnboarding: null,
    })
  }

  const setPendingDesktopOnboarding = (data: PendingDesktopOnboarding) => {
    clearTimer()
    setState({
      user: null,
      session: null,
      loading: false,
      initialized: true,
      authenticated: false,
      error: null,
      errorDetail: null,
      pendingVerification: null,
      pendingPasswordless: null,
      pendingDesktopOnboarding: data,
    })
  }

  const setUnauthenticated = (error: string | null, errorDetail: unknown = null) => {
    clearAccessToken()
    setState({
      user: null,
      session: null,
      loading: false,
      initialized: true,
      authenticated: false,
      offline: false,
      connectionIssue: null,
      error,
      errorDetail,
      pendingVerification: null,
      pendingPasswordless: null,
      pendingDesktopOnboarding: null,
    })
  }

  // A dropped connection is NOT a signed-out state. Mark the app offline WITHOUT touching
  // authenticated/user/session - the user stays exactly where they are. Cleared by markOnline()
  // the moment any auth request succeeds.
  const markOffline = (input: {
    operation: AuthConnectionOperation
    error?: unknown
    reason?: AuthConnectionIssueReason
    retryDelayMs?: number | null
  }) => {
    const reason = input.reason ?? authConnectionReasonForOperation(input.operation, input.error)
    const issue = createAuthConnectionIssue({
      reason,
      operation: input.operation,
      error: input.error,
      retryDelayMs: input.retryDelayMs,
      now: deps.now(),
      previous: state.connectionIssue,
    })
    const changed = state.connectionIssue?.reason !== issue.reason || state.connectionIssue?.operation !== issue.operation
    if (!state.offline || changed) {
      deps.log("[Auth] Connectivity issue; keeping the session and reconnecting in the background", issue)
    }
    setState({
      offline: true,
      connectionIssue: issue,
    })
  }

  const markOnline = () => {
    if (state.offline) {
      deps.log("[Auth] Reconnected to OpenAnalyst")
      setState({
        offline: false,
        connectionIssue: null,
      })
    }
  }

  // Local optimistic update of state.user.fullName from onboarding sign-in.
  // The OA backend may not have the user's name yet (especially for brand
  // new sign-ups) — the sign-in screen captures it, this lets it flow into
  // the empty-screen greeting without a backend round-trip. No-op when
  // backend already returned a fullName OR when there's no user yet.
  const applyLocalProfileName = (fullName: string) => {
    const trimmed = fullName?.trim()
    if (!trimmed) return
    const currentUser = state.user
    if (!currentUser) return
    if ((currentUser.fullName ?? "").trim()) return
    setState("user", { ...currentUser, fullName: trimmed })
  }

  const setPendingVerification = (email: string) => {
    setState({
      user: null,
      session: null,
      loading: false,
      initialized: true,
      authenticated: false,
      error: null,
      errorDetail: null,
      pendingVerification: { email },
      pendingPasswordless: null,
      pendingDesktopOnboarding: null,
    })
  }

  const clearPendingVerification = () => {
    setState("pendingVerification", null)
  }

  const setPendingPasswordless = (input: AuthState["pendingPasswordless"]) => {
    setState({
      user: null,
      session: null,
      loading: false,
      initialized: true,
      authenticated: false,
      error: null,
      errorDetail: null,
      pendingVerification: null,
      pendingPasswordless: input,
      pendingDesktopOnboarding: null,
    })
  }

  const beginAuthIntentPasswordless = (preview: AuthIntentPreview) => {
    pending = ""
    receiveAuthStarterHandoff(preview)
    setPendingPasswordless({
      kind: "auth-intent",
      authIntentId: preview.authIntentId,
      destination: preview.destination ?? null,
      title: preview.title ?? null,
      subtitle: preview.subtitle ?? null,
      partnerDisplayName: preview.partnerDisplayName ?? null,
      expiresAt: preview.expiresAt ?? null,
      ...(preview.starterIntent ? { starterIntent: preview.starterIntent } : {}),
    })
  }

  const scheduleRefresh = (exp: number) => {
    clearTimer()
    refreshRetries = 0
    const delay = exp * 1000 - deps.now() - REFRESH_BUFFER_MS
    if (delay <= 0) {
      timer = deps.setTimeout(() => {
        void refreshSession()
      }, 0)
      return
    }
    timer = deps.setTimeout(() => {
      void refreshSession()
    }, delay)
  }

  /**
   * The network was down when we tried to refresh. Try again shortly — do not sign anyone out.
   *
   * Backs off 5s → 10s → 20s → 40s → 60s and then stays there, retrying for as long as the app is
   * open. There is no attempt limit on purpose: a laptop can be shut in a bag for an hour, and the
   * right behaviour on reopening is that it just works. Giving up after N tries would only convert
   * a long outage into the sign-out this whole change exists to prevent.
   */
  const scheduleRefreshRetry = () => {
    clearTimer()
    const delay = Math.min(REFRESH_RETRY_BASE_MS * 2 ** refreshRetries, REFRESH_RETRY_MAX_MS)
    refreshRetries += 1
    deps.log("[Auth] Refresh unavailable; retrying", { delay })
    timer = deps.setTimeout(() => {
      void refreshSession()
    }, delay)
    return delay
  }

  const getCachedProfile = (): api.AuthProfile | null => {
    try {
      const raw = sessionStorage.getItem(PROFILE_CACHE_KEY)
      if (!raw) return null
      return JSON.parse(raw) as api.AuthProfile
    } catch {
      return null
    }
  }

  const cacheProfile = (profile: api.AuthProfile) => {
    try {
      sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile))
    } catch {}
  }

  const hydrateSession = async () => {
    let session: AuthSession | null
    try {
      session = await deps.checkSession()
    } catch (error) {
      throw tagAuthConnectionOperation(error, "session_check")
    }
    if (!session) return null

    const cached = getCachedProfile()
    if (cached && (cached.userId === session.userId || cached._id === session.userId)) {
      deps.getUserProfile(session.userId).then(cacheProfile).catch(() => {})
      return { session, user: mapUser(session, cached) }
    }

    let profile: api.AuthProfile
    try {
      profile = await deps.getUserProfile(session.userId)
    } catch (error) {
      throw tagAuthConnectionOperation(error, "profile_fetch")
    }
    cacheProfile(profile)
    return {
      session,
      user: mapUser(session, profile),
    }
  }

  const applySession = async () => {
    let desktopRestoreFailed = false
    if (!getAccessToken() && deps.isDesktop) {
      try {
        await deps.restoreDesktopToken()
      } catch (error) {
        // An outage is not a reason to log anyone out.
        //
        // `deps.logout()` below is not a local reset — it reaches into the main process and
        // DELETES the on-disk session, refresh cookie included. Running that because restore
        // could not reach the network destroyed logins that were never invalid, and destroyed
        // them permanently: the refresh token that would have fixed everything on the next boot
        // went with it. The main process now refuses to wipe on a transient failure and throws
        // instead — so if we wiped here on that throw, we would simply be doing its old job for
        // it. Leave the stored session alone; the next boot with a network just works.
        if (isTransientAuthError(error)) {
          // The stored session is intact — we just could not reach the network to mint an access
          // token from it. Do NOT sign out (that used to flip the whole app to the login screen for
          // a wifi blink). Mark offline, keep an already-signed-in user exactly where they are, and
          // retry in the background; the next reachable moment restores them with no interaction.
          clearAccessToken()
          if (!everAuthenticated) setState({ loading: false, initialized: true })
          const retryDelayMs = scheduleRefreshRetry()
          markOffline({ operation: "desktop_auth_restore", error, retryDelayMs })
          return false
        }
        deps.log("[Auth] Desktop auth restore failed; clearing stale local auth", error)
        try {
          await deps.logout()
        } catch (logoutError) {
          deps.log("[Auth] Desktop auth cleanup failed", logoutError)
        }
        clearAccessToken()
        desktopRestoreFailed = true
      }
    }
    if (desktopRestoreFailed) {
      clearTimer()
      setUnauthenticated(null)
      return false
    }
    if (!getAccessToken()) {
      await refreshAccessToken().catch(() => {})
    }

    const result = getAccessToken() ? await hydrateSession() : null
    if (!result) {
      clearTimer()
      setUnauthenticated(null)
      return false
    }
    setAuthenticated(result.session, result.user)
    // WHY DESKTOP REFRESHES ON A TIMER TOO.
    //
    // This used to be `if (!deps.isDesktop)`, dating from the original auth migration with no
    // stated reason. The effect: desktop had NO proactive refresh at all. The session simply aged
    // past its expiry while the app sat open, and the only thing that ever rescued it was some
    // request happening to 401 and triggering the reactive path. Leave the app idle over lunch —
    // no requests, nothing to 401 — and the next thing you touched met an expired token and sent
    // you to sign in again. That is the "long session asks me to sign in" report.
    //
    // Refreshing two minutes before expiry is exactly what the web has always done. It is safe to
    // turn on here only because the failure path above no longer signs people out: a refresh that
    // cannot reach the network now retries with backoff instead of clearing the session. Enabling
    // this timer WITHOUT that fix would have been strictly worse than leaving it off — it would
    // have turned every network blip into an eager logout.
    scheduleRefresh(result.session.exp)

    return true
  }

  const refreshSession = async () => {
    try {
      await deps.refreshAccess()
      const ok = await applySession()
      if (ok) {
        markOnline()
        return true
      }
      // applySession returned false without throwing: on a transient outage it already marked the
      // app offline and scheduled a retry — leave the session alone. Only a genuine "no session"
      // (never offline) is a real sign-out.
      if (state.offline) return false
      deps.clearAllAuthData()
      return false
    } catch (error) {
      // "Session expired. Please login again." was this app's answer to a blinking wifi.
      //
      // Everything in the `try` above touches the network — `refreshAccess`, and `applySession`'s
      // own `checkSession` + `getUserProfile` calls. So a profile endpoint having a bad second,
      // or a tunnel dropping, was indistinguishable from a genuinely expired session, and both
      // ended in `clearAllAuthData()`. The user was signed out, told their session expired, and
      // it had not. Retry instead, and leave the session exactly where it is: an outage is not
      // evidence about anyone's credentials.
      if (isTransientAuthError(error)) {
        const retryDelayMs = scheduleRefreshRetry()
        markOffline({
          operation: authConnectionOperation(error) ?? "auth_refresh",
          error,
          retryDelayMs,
        })
        return false
      }
      deps.log("[Auth] Refresh failed", error)
      deps.clearAllAuthData()
      clearTimer()
      setUnauthenticated("Session expired. Please login again.", error)
      return false
    }
  }

  const initialize = async () => {
    if (!unlisten) {
      unlisten = deps.onLogoutBroadcast(() => {
        deps.clearAllAuthData()
        clearTimer()
        setUnauthenticated(null)
        deps.navigate(LOGIN_PATH)
      })
    }
    // The OS/browser tells us the moment the link drops or returns — surface it immediately rather
    // than waiting for a request to fail. `offline` shows the banner right away; `online` short-
    // circuits the backoff and reconnects now. navigator.onLine can be over-optimistic (a router
    // with no internet still reads "online"), so it only ever HINTS — the authoritative offline/
    // online transitions are still the request that actually fails or succeeds.
    if (!unlistenConnectivity && typeof window !== "undefined" && typeof window.addEventListener === "function") {
      const onOffline = () => {
        if (state.authenticated || everAuthenticated) {
          markOffline({
            operation: "navigator_offline",
            reason: "device_offline",
            error: "navigator: offline",
          })
        }
      }
      const onOnline = () => {
        if (!state.offline) return
        deps.log("[Auth] Network returned — reconnecting now")
        void refreshSession()
      }
      window.addEventListener("offline", onOffline)
      window.addEventListener("online", onOnline)
      unlistenConnectivity = () => {
        window.removeEventListener("offline", onOffline)
        window.removeEventListener("online", onOnline)
      }
    }
    setState("loading", true)
    try {
      await applySession()
    } catch (error) {
      const onboarding = desktopOnboardingData(error)
      if (onboarding) {
        setPendingDesktopOnboarding(onboarding)
        return
      }
      // A network outage during restore is not evidence the session is gone — don't sign out over
      // it. Keep an already-signed-in user in the app, show the offline banner, and retry.
      if (isTransientAuthError(error)) {
        if (!everAuthenticated) setState({ loading: false, initialized: true })
        const retryDelayMs = scheduleRefreshRetry()
        markOffline({
          operation: authConnectionOperation(error) ?? "session_check",
          error,
          retryDelayMs,
        })
        return
      }
      deps.log("[Auth] Initialization error", error)
      clearTimer()
      setUnauthenticated("Session could not be restored. Sign in again to reconnect OpenAnalyst.", error)
    }
  }

  const checkSession = async () => {
    setState("loading", true)
    try {
      return await applySession()
    } catch (error) {
      const onboarding = desktopOnboardingData(error)
      if (onboarding) {
        setPendingDesktopOnboarding(onboarding)
        return false
      }
      // Same rule as initialize(): a dropped connection keeps the session, it does not end it.
      if (isTransientAuthError(error)) {
        if (!everAuthenticated) setState({ loading: false, initialized: true })
        const retryDelayMs = scheduleRefreshRetry()
        markOffline({
          operation: authConnectionOperation(error) ?? "session_check",
          error,
          retryDelayMs,
        })
        return false
      }
      deps.log("[Auth] Session check failed", error)
      clearTimer()
      setUnauthenticated(toMessage(error, "Session could not be checked. Sign in again to reconnect OpenAnalyst."), error)
      return false
    }
  }

  const login = (returnTo?: string) => {
    if (returnTo) deps.storeReturnTo(returnTo)
    if (!returnTo && typeof window !== "undefined") {
      const path = window.location.pathname + window.location.search
      if (path !== "/" && !path.startsWith("/login") && !path.startsWith("/callback")) {
        deps.storeReturnTo(path)
      }
    }
    deps.navigate(LOGIN_PATH)
  }

  const signInWithPassword = async (email: string, password: string) => {
    const started = await startPasswordSignIn(email)
    if (!started) return false
    return completePasswordSignIn(password)
  }

  const startPasswordlessSignIn = async (email: string, intent: PasswordlessIntent = "signin") => {
    setState("loading", true)
    setState("error", null)
    setState("errorDetail", null)
    setState("pendingPasswordless", null)
    setState("pendingDesktopOnboarding", null)
    pending = email.trim().toLowerCase()
    try {
      const data = await deps.startPasswordlessSignIn(pending, intent)
      const session = data.session?.trim()
      if (passwordlessStartReturnedToken(data) && getAccessToken()) {
        const ok = await applySession()
        if (ok) {
          pending = ""
          return true
        }
        if (state.pendingDesktopOnboarding) {
          pending = ""
          return true
        }
        setState("error", "Sign in succeeded but no session was found")
        return false
      }
      if (!session) {
        pending = ""
        throw new api.AuthApiError({
          status: 400,
          message: "10x sign-in did not return a verification session.",
          code: "MISSING_PASSWORDLESS_SESSION",
        })
      }
      setPendingPasswordless({
        kind: "direct",
        email: pending,
        session,
        intent,
        destination:
          data.codeDeliveryDetails?.destination ??
          data.codeDelivery?.destination ??
          null,
      })
      return true
    } catch (error) {
      pending = ""
      setState("loading", false)
      setState("error", toMessage(error, "We couldn't send a sign-in code. Check the email address and try again."))
      setState("errorDetail", error)
      setState("pendingPasswordless", null)
      setState("pendingDesktopOnboarding", null)
      return false
    }
  }

  const completePasswordlessSignIn = async (code: string) => {
    const current = state.pendingPasswordless
    if (!current) {
      setState("error", "Start sign in again to request a new code.")
      return false
    }
    setState("loading", true)
    setState("error", null)
    setState("errorDetail", null)
    try {
      await deps.confirmPasswordlessSignIn(
        current.kind === "auth-intent"
          ? { authIntentId: current.authIntentId, code: code.trim() }
          : {
              email: current.email,
              code: code.trim(),
              session: current.session,
              intent: current.intent,
            },
      )
      const ok = await applySession()
      if (ok) {
        pending = ""
        setState("pendingPasswordless", null)
        return true
      }
      setState("error", "Sign in succeeded but no session was found")
      return false
    } catch (error) {
      const onboarding = desktopOnboardingData(error)
      if (onboarding) {
        pending = ""
        setPendingDesktopOnboarding(onboarding)
        return true
      }
      setState("loading", false)
      setState("error", toMessage(error, "We couldn't verify that code. Use the latest code from your email and try again."))
      setState("errorDetail", error)
      if (current.kind === "direct") {
        const retrySession = passwordlessRetrySession(error)
        if (retrySession) {
          setPendingPasswordless({ ...current, session: retrySession })
        }
      }
      if (current.kind === "auth-intent" && isTerminalAuthIntentError(error)) {
        pending = ""
        clearAuthStarterHandoff()
        setState("pendingPasswordless", null)
      }
      return false
    }
  }

  const cancelPasswordlessSignIn = () => {
    pending = ""
    if (state.pendingPasswordless?.kind === "auth-intent") clearAuthStarterHandoff()
    setState("pendingPasswordless", null)
    setState("pendingDesktopOnboarding", null)
    setState("error", null)
    setState("errorDetail", null)
  }

  const startPasswordSignIn = async (email: string) => {
    setState("loading", true)
    setState("error", null)
    setState("errorDetail", null)
    setState("pendingDesktopOnboarding", null)
    pending = email.trim()
    try {
      const data = await deps.initAuth(pending)
      if (data.allowedMethods.length > 0 && !data.allowedMethods.includes("password")) {
        pending = ""
        deps.clearSessionToken()
        setState("loading", false)
        setState("error", "Password sign-in is not available for this account.")
        return false
      }
      if (!deps.getSessionToken()) {
        pending = ""
        throw new api.AuthApiError({
          status: 400,
          message: "Authentication init failed: missing session token",
          code: "MISSING_SESSION_TOKEN",
        })
      }
      setState("loading", false)
      return true
    } catch (error) {
      pending = ""
      setState("loading", false)
      setState("error", toMessage(error, "Password sign-in could not start. Restart OpenAnalyst and try again."))
      setState("errorDetail", error)
      return false
    }
  }

  const completePasswordSignIn = async (password: string) => {
    setState("loading", true)
    setState("error", null)
    setState("errorDetail", null)
    setState("pendingDesktopOnboarding", null)
    try {
      const result = await deps.authenticateWithPassword(password, pending || undefined)
      const emailUsed = result.email || pending

      if (!result.isEmailVerified) {
        pending = ""
        setPendingVerification(emailUsed)
        return false
      }

      const ok = await applySession()
      if (ok) {
        pending = ""
        return true
      }
      setState("error", "Sign in succeeded but no session was found")
      return false
    } catch (error) {
      setState("loading", false)
      setState("error", toMessage(error, "Sign in failed. Check your email and password, then try again."))
      setState("errorDetail", error)
      return false
    }
  }

  const cancelPasswordSignIn = () => {
    pending = ""
    deps.clearSessionToken()
    setState("error", null)
    setState("errorDetail", null)
    setState("pendingPasswordless", null)
    setState("pendingDesktopOnboarding", null)
  }

  const signInWithGoogle = async (credential: string) => {
    setState("loading", true)
    setState("error", null)
    setState("errorDetail", null)
    setState("pendingDesktopOnboarding", null)
    try {
      const result = await deps.signInWithGoogle(credential)

      if (!result.isEmailVerified && result.email) {
        setPendingVerification(result.email)
        return false
      }

      const ok = await applySession()
      if (ok) return true
      setState("error", "Sign in succeeded but no session was found")
      return false
    } catch (error) {
      setState("loading", false)
      setState("error", toMessage(error, "Google sign-in failed. Try again."))
      setState("errorDetail", error)
      return false
    }
  }

  const signUp = async (input: SignUpInput) => {
    setState("loading", true)
    setState("error", null)
    setState("errorDetail", null)
    setState("pendingDesktopOnboarding", null)
    try {
      const result = await deps.signUp(input)

      if (!result.isEmailVerified) {
        setPendingVerification(result.email)
        return false
      }

      const ok = await applySession()
      if (ok) return true
      setState("error", "Sign up succeeded but no session was found")
      return false
    } catch (error) {
      setState("loading", false)
      setState("error", toMessage(error, "We couldn't create your account. Check the details and try again."))
      setState("errorDetail", error)
      return false
    }
  }

  const forgotPassword = async (email: string) => {
    await deps.forgotPassword(email)
  }

  const resetPassword = async (token: string, password: string) => {
    await deps.resetPassword(token, password)
  }

  const verifyEmail = async (token: string) => {
    setState("loading", true)
    setState("error", null)
    setState("errorDetail", null)
    setState("pendingDesktopOnboarding", null)
    try {
      const result = await deps.verifyEmail(token)

      if (result.success || result.alreadyVerified) {
        clearPendingVerification()
        const ok = await applySession()
        if (ok) return { success: true, alreadyVerified: result.alreadyVerified }
      }

      return { success: result.success, alreadyVerified: result.alreadyVerified, message: result.message }
    } catch (error) {
      setState("loading", false)
      setState("error", toMessage(error, "Email verification failed. Request a new link and try again."))
      setState("errorDetail", error)
      return { success: false, message: toMessage(error, "Email verification failed. Request a new link and try again.") }
    }
  }

  const resendVerification = async (email: string) => {
    setState("loading", true)
    setState("error", null)
    setState("errorDetail", null)
    try {
      const result = await deps.resendVerification(email)
      setState("loading", false)
      return result
    } catch (error) {
      setState("loading", false)
      setState("error", toMessage(error, "We couldn't resend the verification email. Check the address and try again."))
      setState("errorDetail", error)
      throw error
    }
  }

  const logout = async () => {
    clearTimer()
    try {
      await deps.logout()
    } catch (error) {
      deps.log("[Auth] Logout request failed", error)
    }
    clearAccessToken()
    try {
      sessionStorage.removeItem(PROFILE_CACHE_KEY)
    } catch {}
    deps.broadcastLogout()
    deps.clearAllAuthData()
    setUnauthenticated(null)
    deps.navigate(LOGIN_PATH)
  }

  const destroy = () => {
    clearTimer()
    unlisten?.()
    unlisten = null
    unlistenConnectivity?.()
    unlistenConnectivity = null
  }

  return {
    state: () => state,
    ready,
    login,
    logout,
    checkSession,
    startPasswordlessSignIn,
    beginAuthIntentPasswordless,
    completePasswordlessSignIn,
    cancelPasswordlessSignIn,
    signInWithPassword,
    startPasswordSignIn,
    completePasswordSignIn,
    cancelPasswordSignIn,
    signInWithGoogle,
    signUp,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    clearPendingVerification,
    isAuthenticated: () => state.authenticated,
    wasEverAuthenticated: () => everAuthenticated,
    isOffline: () => state.offline,
    connectionIssue: () => state.connectionIssue,
    applyLocalProfileName,
    initialize,
    destroy,
  }
}

export const { use: useAuth, provider: AuthProvider } = createSimpleContext({
  name: "Auth",
  gate: false,
  init: (): AuthContextValue => {
    const platform = usePlatform()
    const isDesktop = platform.platform === "desktop"

    if (DEV_AUTH_BYPASS && !isDesktop) {
      const state: AuthState = {
        user: {
          userId: "local-dev-user",
          email: "dev@local.openanalyst",
          fullName: "Local Dev",
          orgId: "local-dev-org",
          workspaceId: "local-dev-workspace",
          role: "owner",
        },
        session: {
          userId: "local-dev-user",
          email: "dev@local.openanalyst",
          orgId: "local-dev-org",
          workspaceId: "local-dev-workspace",
          role: "owner",
          bindingTenantId: "local-dev-tenant",
          bindingVersion: 1,
          accountType: "individual",
          exp: Math.floor(Date.now() / 1000) + 365 * 24 * 60 * 60,
        },
        loading: false,
        initialized: true,
        authenticated: true,
        offline: false,
        connectionIssue: null,
        error: null,
        errorDetail: null,
        pendingVerification: null,
        pendingPasswordless: null,
        pendingDesktopOnboarding: null,
      }

      return {
        state: () => state,
        ready: () => true,
        login: () => {},
        logout: async () => {},
        checkSession: async () => true,
        startPasswordlessSignIn: async () => true,
        beginAuthIntentPasswordless: () => {},
        completePasswordlessSignIn: async () => true,
        cancelPasswordlessSignIn: () => {},
        signInWithPassword: async () => true,
        startPasswordSignIn: async () => true,
        completePasswordSignIn: async () => true,
        cancelPasswordSignIn: () => {},
        signInWithGoogle: async () => true,
        signUp: async () => true,
        forgotPassword: async () => {},
        resetPassword: async () => {},
        verifyEmail: async () => ({ success: true, alreadyVerified: true }),
        resendVerification: async () => ({ success: true, message: "Verification skipped in local dev." }),
        clearPendingVerification: () => {},
        isAuthenticated: () => true,
        wasEverAuthenticated: () => true,
        isOffline: () => false,
        connectionIssue: () => null,
        applyLocalProfileName: () => {}, // bypass — already has fullName
      }
    }

    if (isDesktop) {
      const desktopHostedFetch = platform.smartworkControlPlaneFetch ?? platform.fetch ?? null
      configureDesktopMode(true)
      configureDesktopAccountFetch(desktopHostedFetch)
      configureMcpFetch(desktopHostedFetch)
      configureOnboardingFetch(desktopHostedFetch)
      configureOnboardingV2Fetch(desktopHostedFetch)
      configureBillingFetch(desktopHostedFetch)
      configureCreditBalanceFetch(desktopHostedFetch)
      configureTranscriptionFetch(desktopHostedFetch)
      configureDesktopRefresh({
        refresh: async () => await platform.openAnalystAuthRefresh?.() ?? null,
        clearTokens: async () => {
          await platform.openAnalystAuthLogout?.()
        },
      })
    }

    const controller = createAuthController({
      isDesktop,
      navigate: isDesktop ? () => {} : undefined,
      startPasswordlessSignIn: isDesktop && platform.openAnalystPasswordlessStart
        ? async (email, intent) => {
          const result = await platform.openAnalystPasswordlessStart!({ email, intent })
          await api.applyAuthTokens(result)
          return result
        }
        : api.startPasswordlessSignIn,
      confirmPasswordlessSignIn: isDesktop && platform.openAnalystPasswordlessConfirm
        ? async (input) => {
          const result = await platform.openAnalystPasswordlessConfirm!(input)
          await api.applyAuthTokens(result)
          return result
        }
        : api.confirmPasswordlessSignIn,
      logout: isDesktop && platform.openAnalystAuthLogout
        ? async () => {
          await platform.openAnalystAuthLogout!()
        }
        : api.logout,
      restoreDesktopToken: async () => {
        if (!isDesktop) return
        const restored = await platform.openAnalystAuthRestore?.()
        if (restored) {
          // A restorable desktop session means this install has signed in
          // before — a RETURNING user. Record the install-level onboarding
          // marker so first-run onboarding never hijacks /login or /signup
          // for them, even when renderer localStorage was cleared (the
          // main-process auth store this restore reads from survives that).
          markOnboardingV2Completed(null)
          await api.applyAuthTokens(restored)
        }
      },
    })
    onMount(() => {
      void controller.initialize()
    })
    onCleanup(() => {
      controller.destroy()
      if (isDesktop) {
        configureDesktopAccountFetch(null)
        configureMcpFetch(null)
        configureOnboardingFetch(null)
        configureOnboardingV2Fetch(null)
        configureBillingFetch(null)
        configureCreditBalanceFetch(null)
        configureTranscriptionFetch(null)
        resetDesktopRefresh()
      }
    })
    return controller
  },
})

export type { AuthContextValue, AuthSession, AuthState, AuthUser, SignUpInput } from "./types"
export { getReturnTo, clearReturnTo, storeReturnTo } from "./storage"
